import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb, { getAboutBreadcrumb } from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q9d4fnahb7382ov85g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const totalArticles = articles.length;
  const totalCategories = 5;

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于我们 - 磁力链指南',
          description: '磁力链指南是一个专注于磁力链使用教程、搜索技巧和客户端推荐的综合性平台。',
          url: 'https://magnetlink-guide.com/about',
          inLanguage: 'zh-CN',
        })}
      </script>

      {/* Page Header */}
      <section className="pt-24 pb-10 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="mb-4">
            <Breadcrumb items={getAboutBreadcrumb()} />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">关于我们</h1>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                品牌介绍
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                我们是谁
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  磁力链指南是一个专注于磁力链使用教程、搜索技巧和客户端推荐的综合性中文平台。我们致力于为国内用户提供最全面、最易懂的磁力链相关知识，帮助每一位用户快速掌握磁力链的完整使用流程。
                </p>
                <p>
                  自成立以来，我们已累计发布<strong>{totalArticles}</strong>篇深度教程文章，覆盖<strong>{totalCategories}</strong>大核心内容分类，涵盖磁力链工作原理、客户端选择、搜索技巧、常见问题及资源导航等方方面面。
                </p>
                <p>
                  我们的团队由一群热爱技术分享的P2P网络爱好者组成。我们相信，去中心化的资源分享技术代表了互联网的未来发展方向，而磁力链作为其中的核心技术，值得被更多人了解和正确使用。
                </p>
              </div>
            </div>

            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20workspace%20with%20team%20working%20on%20digital%20projects%2C%20warm%20amber%20lighting%2C%20clean%20minimalist%20office%2C%20professional%20atmosphere%2C%20soft%20cream%20and%20teal%20accents%2C%20creative%20tech%20environment&width=800&height=600&seq=about-brand-magnet&orientation=landscape"
                alt="磁力链指南团队 - 专业的技术分享平台"
                title="磁力链指南团队"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Reputation */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4">
              平台数据
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              值得信赖的内容平台
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              用数据说话，持续为中文用户提供高质量的磁力链使用指南
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { value: `${totalArticles}`, label: '深度文章', icon: 'ri-article-line' },
              { value: `${totalCategories}`, label: '内容分类', icon: 'ri-folder-line' },
              { value: '50万+', label: '索引资源', icon: 'ri-database-2-line' },
              { value: '99.7%', label: '内容好评率', icon: 'ri-heart-line' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-4">
            我们的使命
          </span>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
            让每个人都能轻松使用磁力链
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-8">
            我们始终相信，技术应该为所有人服务。无论是技术新手还是资深用户，都能在磁力链指南找到适合自己的内容。我们坚持客观、专业、易懂的内容创作原则，持续更新和优化每一篇文章，确保信息的准确性和时效性。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {['专业可靠', '内容全面', '更新及时', '通俗易懂'].map((tag) => (
              <span
                key={tag}
                className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 border border-background-200/70 text-foreground-700"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
              联系我们
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              有任何问题？欢迎留言
            </h2>
            <p className="text-sm text-foreground-600">
              无论是使用问题、内容建议还是合作意向，我们都期待听到你的声音
            </p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 space-y-5">
            <input
              type="text"
              name="company_alt"
              className="form-hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                  placeholder="请输入你的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                  邮箱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                  placeholder="请输入你的邮箱"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                主题
              </label>
              <select
                id="contact-subject"
                name="subject"
                className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
              >
                <option value="general">一般咨询</option>
                <option value="suggestion">内容建议</option>
                <option value="cooperation">商务合作</option>
                <option value="bug">问题反馈</option>
                <option value="other">其他</option>
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                留言内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                placeholder="请输入你的留言内容（最多500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500字</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap text-sm cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>
            </div>

            {formStatus === 'success' && (
              <div className="p-4 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-700">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                {formMessage}
              </div>
            )}

            {formStatus === 'error' && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-error-warning-line"></i>
                </span>
                {formMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-10 bg-background-50 border-t border-background-200/70">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-foreground-500">
              想要了解更多磁力链知识？从这里开始：
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Link
                to="/category/tutorials"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 no-underline"
              >
                使用教程
              </Link>
              <Link
                to="/category/clients"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 no-underline"
              >
                客户端推荐
              </Link>
              <Link
                to="/category/search-tips"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 no-underline"
              >
                搜索技巧
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}